import { useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ekycApi } from '../api';

const STEP_ORDER = ['OCR', 'FACE_MATCH', 'LIVENESS', 'CONFIRM', 'COMPLETED'];

const resolveStepIndex = (stepKey, steps) => {
  const idx = steps.indexOf(stepKey);
  return idx >= 0 ? idx : 0;
};

const formatError = (err) => {
  const data = err.data;
  if (data?.message && data?.code) {
    return { code: data.message, message: data.code };
  }
  return { code: null, message: err.message || 'Đã xảy ra lỗi' };
};

const applySessionData = (data, setters) => {
  const { setSessionId, setSessionStatus, setStepsRequired, setExtractedData, setValidations, setRawResponse } = setters;
  if (data.sessionId) setSessionId(data.sessionId);
  if (data.status) setSessionStatus(data.status);
  if (data.stepsRequired?.length > 0) setStepsRequired(data.stepsRequired);
  if (data.extractedData) setExtractedData(data.extractedData);
  if (data.validations) setValidations(data.validations);
  if (data.rawResponse !== undefined) setRawResponse(data.rawResponse);
};

const INIT_STATE = {
  IDLE: 'IDLE',
  CHECKING: 'CHECKING',
  NO_SESSION: 'NO_SESSION',
  READY: 'READY',
};

export default function useEkyc() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionId, setSessionId] = useState(null);
  const [currentStep, setCurrentStep] = useState(-1);
  const [stepsRequired, setStepsRequired] = useState([]);
  const [extractedData, setExtractedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [validations, setValidations] = useState([]);
  const [rawResponse, setRawResponse] = useState(null);
  const [initState, setInitState] = useState(INIT_STATE.IDLE);
  const initRef = useRef(false);

  const visibleSteps = stepsRequired.length > 0
    ? [...stepsRequired.filter(s => STEP_ORDER.includes(s) && s !== 'FACE_MATCH'), 'CONFIRM']
    : ['OCR', 'LIVENESS', 'CONFIRM'];

  const setters = { setSessionId, setSessionStatus, setStepsRequired, setExtractedData, setValidations, setRawResponse };

  const persistSession = useCallback((uuid) => {
    setSearchParams({ session: uuid }, { replace: true });
  }, [setSearchParams]);

  const resolveNextStep = useCallback((nextStepKey, fallback) => {
    const key = nextStepKey || fallback;
    const idx = visibleSteps.indexOf(key);
    setCurrentStep(idx >= 0 ? idx : currentStep + 1);
  }, [visibleSteps, currentStep]);

  const applyStatusData = useCallback((data) => {
    applySessionData(data, setters);
    if (data.sessionUuid) {
      persistSession(data.sessionUuid);
    }
    const status = data.status;
    if (status === 'VERIFIED' || status === 'CONFIRMED') {
      setCurrentStep(visibleSteps.length);
    } else {
      const nextIdx = data.nextStep
        ? resolveStepIndex(data.nextStep, visibleSteps)
        : 0;
      setCurrentStep(nextIdx);
    }
    setInitState(INIT_STATE.READY);
  }, [visibleSteps, persistSession]);

  const checkActiveSession = useCallback(async () => {
    if (initRef.current) return;
    initRef.current = true;
    setInitState(INIT_STATE.CHECKING);
    try {
      const res = await ekycApi.getStatus();
      applyStatusData(res.data);
    } catch {
      initRef.current = false;
      setInitState(INIT_STATE.NO_SESSION);
    }
  }, [applyStatusData]);

  const initSession = useCallback(async (providerCode) => {
    if (initRef.current) return;
    initRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await ekycApi.initSession(providerCode);
      const data = res.data;
      applySessionData(data, setters);
      if (data.sessionUuid) {
        persistSession(data.sessionUuid);
      }
      const nextIdx = data.stepsCompleted?.length > 0
        ? resolveStepIndex(data.nextStep || 'OCR', visibleSteps)
        : 0;
      setCurrentStep(nextIdx);
      setInitState(INIT_STATE.READY);
      return data;
    } catch (err) {
      initRef.current = false;
      setError(formatError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [visibleSteps, persistSession]);

  const restoreSession = useCallback(async () => {
    if (initRef.current) return;
    initRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await ekycApi.getStatus();
      applyStatusData(res.data);
      return res.data;
    } catch (err) {
      initRef.current = false;
      setError(formatError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [applyStatusData]);

  const getSessionFromUrl = useCallback(() => {
    return searchParams.get('session');
  }, [searchParams]);

  const submitOcr = useCallback(async (frontCard, backCard) => {
    setLoading(true);
    setError(null);
    try {
      const res = await ekycApi.submitOcr(frontCard, backCard);
      const data = res.data;
      if (data.extractedData) setExtractedData(data.extractedData);
      if (data.validations) setValidations(data.validations);
      resolveNextStep(data.nextStep, 'LIVENESS');
      return data;
    } catch (err) {
      setError(formatError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [resolveNextStep]);

  const submitFaceMatch = useCallback(async (selfie) => {
    setLoading(true);
    setError(null);
    try {
      const res = await ekycApi.submitFaceMatch(selfie);
      const data = res.data;
      resolveNextStep(data.nextStep, 'CONFIRM');
      return data;
    } catch (err) {
      setError(formatError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [resolveNextStep]);

  const submitLiveness = useCallback(async (video) => {
    setLoading(true);
    setError(null);
    try {
      const res = await ekycApi.submitLiveness(video);
      const data = res.data;
      resolveNextStep(data.nextStep, 'CONFIRM');
      return data;
    } catch (err) {
      setError(formatError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [resolveNextStep]);

  const confirm = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ekycApi.confirm();
      const data = res.data;
      setSessionStatus(data.status);
      if (data.extractedData) setExtractedData(data.extractedData);
      setCurrentStep(visibleSteps.length);
      return data;
    } catch (err) {
      setError(formatError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [visibleSteps]);

  const getProfile = useCallback(async () => {
    try {
      const res = await ekycApi.getProfile();
      return res.data;
    } catch (err) {
      setError(formatError(err));
      throw err;
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const restartFlow = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await ekycApi.cancelSession();
    } catch {
    }
    initRef.current = false;
    setSessionId(null);
    setCurrentStep(-1);
    setStepsRequired([]);
    setExtractedData(null);
    setValidations([]);
    setRawResponse(null);
    setSessionStatus(null);
    setLoading(false);
    try {
      await initSession();
    } catch {
    }
  }, [initSession]);

  const reset = useCallback(() => {
    initRef.current = false;
    setSessionId(null);
    setCurrentStep(-1);
    setStepsRequired([]);
    setExtractedData(null);
    setError(null);
    setSessionStatus(null);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const isCompleted = currentStep >= visibleSteps.length;

  return {
    sessionId,
    currentStep,
    visibleSteps,
    stepsRequired,
    extractedData,
    validations,
    rawResponse,
    loading,
    error,
    sessionStatus,
    isCompleted,
    initState,
    checkActiveSession,
    initSession,
    restoreSession,
    getSessionFromUrl,
    submitOcr,
    submitFaceMatch,
    submitLiveness,
    confirm,
    getProfile,
    clearError,
    restartFlow,
    reset,
  };
}
